import React, { useState, useCallback, useRef } from "react";
import { ProgressBar } from "primereact/progressbar";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import {useTranslate} from "../system/useTranslate";
import api, {normalizeApiError} from "../system/api";
import DropdownItemTemplate from "../system/components/dropdown-item-template";
import {saveUser} from "./auth-slice";
import {Password} from "primereact/password";
import {isProfilePasswordValid, profilePasswordError} from "./profile-password-validation";
import {Message} from "primereact/message";
import BrandLogo from "../system/components/brand-logo";

// ----------------------
// Shell مشترک برای همه استپ‌ها
// ----------------------
function StepShell({children, showPrev = true, onPrev, onNext, prevLabel, nextLabel, nextDisabled, loading = false}) {
    const translate = useTranslate();

    return (
        <form className="w-full flex flex-column gap-5" onSubmit={(event) => {
            event.preventDefault();
            if (!nextDisabled && !loading) onNext?.();
        }}>
            <div className="flex flex-column gap-4 align-items-center rtl w-full">
                {children}
            </div>
            <div className="w-full flex flex-column-reverse sm:flex-row align-items-center justify-content-between gap-3 pt-4 border-top-1 border-100">
                {showPrev ? (<Button type="button" className="w-full sm:w-auto text-xs gap-2 px-4 py-3" severity="secondary" text icon="pi pi-arrow-right" label={prevLabel || translate("previous")} onClick={onPrev} disabled={loading}/>) : (<span />)}
                <Button type="submit" className="w-full sm:w-auto text-xs gap-2 px-6 py-3" icon="pi pi-arrow-left" iconPos="right" label={nextLabel || translate("next")} disabled={nextDisabled || loading} loading={loading}/>
            </div>
        </form>
    );
}


export function UpdateUserProfile({onCreated, welcomeStep = false}) {
    const translate = useTranslate();
    const [index, setIndex] = useState(0);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const submitLockRef = useRef(false);
    const {user} = useSelector(state => state.auth);
    const [wizardData, setWizardData] = useState({});

    let items = [
        { label: translate("init-profile-step-1"), className: "text-xs" },
        { label: translate("init-profile-step-2"), className: "text-xs" },
        { label: translate("init-profile-step-3"), className: "text-xs" },
        { label: translate("init-profile-step-4"), className: "text-xs" },
        // { label: translate("init-profile-step-5"), className: "text-xs" },
    ];
    if (!welcomeStep)items=items.slice(1);

    const goNext = () => setIndex((prev) => Math.min(prev + 1, items.length - 1));
    const goPrev = () => setIndex((prev) => Math.max(prev - 1, 0));

    const updateUser = useCallback((patch) => {
        setWizardData((prev) => ({...prev, ...(prev || {}), ...patch}));
    }, []);

    const updateWizardData = (patch)=>{
        setWizardData(prev=>({...prev, ...patch}))
    }

    const handleUpdateUser = async (data) => {
        if (loading || submitLockRef.current) return null;
        submitLockRef.current = true;
        setLoading(true);
        setSubmitError(null);
        try {
            const _data = await api.put(`/updateUser`, data);
            dispatch(saveUser({...user, ..._data.data}))
            onCreated?.(_data.data);
            return _data.data;
        } catch (err) {
            const normalized = err?.normalizedError || normalizeApiError(err);
            setSubmitError(normalized.message || "profile-onboarding.error");
            return null;
        } finally {
            submitLockRef.current = false;
            setLoading(false);
        }
    };

    // لیست استپ‌ها به صورت آرایه، برای توسعه خیلی راحت‌تره
    let steps = [
        Step1,
        Step2,
        Step3,
        Step4,
        // Step5,
    ];
    if (!welcomeStep)steps=steps.slice(1);

    const ActiveStep = steps[index];

    return (
        <div className="w-full h-full flex flex-column rtl surface-0 overflow-hidden">
            <header className="w-full flex align-items-center justify-content-between gap-3 px-3 md:px-5 py-4 border-bottom-1 border-100">
                <BrandLogo className="w-10rem sm:w-12rem h-auto"/>
                <div className="flex align-items-center gap-3">
                    <span className="hidden md:inline text-xs text-500">{items[index]?.label}</span>
                    <span className="hidden sm:inline text-xs text-500">{translate("profile-onboarding.progress").replace("{current}", index + 1).replace("{total}", items.length)}</span>
                    <ProgressBar className="w-8rem sm:w-10rem h-1rem" value={((index + 1) / items.length) * 100} showValue={false}/>
                </div>
            </header>
            <main className="flex-1 overflow-auto flex align-items-center justify-content-center p-3 md:p-5">
                <div className="w-full lg:w-8 xl:w-6 flex flex-column align-items-center gap-5">
                    <div className="flex flex-column align-items-center text-center gap-3">
                        <div className="w-4rem h-4rem border-1 border-200 border-circle flex align-items-center justify-content-center">
                            <i className="pi pi-user-edit text-3xl text-primary"/>
                        </div>
                        <h2 className="m-0 text-3xl text-900">{translate("profile-onboarding.title")}</h2>
                        <span className="text-xs text-500 line-height-3">{translate("profile-onboarding.description")}</span>
                    </div>
                    <ActiveStep welcomeStep={welcomeStep} index={index} goNext={goNext} goPrev={goPrev} wizardData={wizardData} updateUser={updateUser} updateWizardData={updateWizardData} onSubmit={handleUpdateUser} loading={loading} submitError={submitError}/>
                </div>
            </main>
        </div>
    );
}

// ----------------------
// کامپوننت اصلی Init
// ----------------------
export function Init({onCreated}) {
    return(
        <div className="w-full h-full overflow-hidden surface-0">
            <UpdateUserProfile onCreated={onCreated} welcomeStep={true}/>
        </div>
    )
}

// ----------------------
// Step 1
// ----------------------
function Step1({ goNext }) {
    const translate = useTranslate();

    const handleNext = () => {
        goNext();
    };

    return (
        <StepShell showPrev={false} onNext={handleNext} nextLabel={translate("lets-go")}>
            <span className="text-700 font-bold w-7 text-center">
                {translate("init-profile-step-1-description")}
            </span>
        </StepShell>
    );
}

// ----------------------
// Step 2 (سه ساب‌استپ داخلی)
// ----------------------
function Step2({ goNext, goPrev, wizardData, updateUser, welcomeStep }) {
    const [subIndex, setSubIndex] = useState(0);

    const commonProps = {subIndex, setSubIndex, goNext, goPrev, wizardData, updateUser};

    if (subIndex === 0) return <Step2SubStep1 {...commonProps} welcomeStep={welcomeStep} />;
    if (subIndex === 1) return <Step2SubStep2 {...commonProps} />;
    // if (subIndex === 2) return <Step2SubStep3 {...commonProps} />;

    return null;
}

function Step2SubStep1({ subIndex, setSubIndex, goPrev, updateUser, welcomeStep }) {
    const {user} = useSelector(state => state.auth);

    const translate = useTranslate();
    const [firstname, setFirstname] = useState(user?.firstname || "");
    const [lastname, setLastname] = useState(user?.lastname || "");
    const [fullname, setFullname] = useState(user?.fullname || "");

    const handleNext = () => {
        updateUser({ firstname, lastname, fullname });
        setSubIndex(subIndex + 1);
    };

    return (
        <StepShell showPrev={welcomeStep} onPrev={goPrev} onNext={handleNext} nextDisabled={!firstname || firstname.length < 2||!lastname || lastname.length < 2||!fullname || fullname.length < 2}>
            <span className="text-sm text-700 font-bold">
                {translate("init-profile-step-2-sub-step-1-description")}
            </span>
            <InputText className="w-full text-sm p-3" value={firstname} onChange={(e) => {setFirstname(e.target.value); setFullname(`${e.target.value} ${lastname}`.trim())}} autoFocus placeholder={translate("firstname-placeholder")}/>
            <InputText className="w-full text-sm p-3" value={lastname} onChange={(e) => {setLastname(e.target.value); setFullname(`${firstname} ${e.target.value}`.trim())}} placeholder={translate("lastname-placeholder")}/>
            <InputText value={fullname} readOnly className="w-full border-none shadow-none"/>
        </StepShell>
    );
}

function Step2SubStep2({ subIndex, setSubIndex, goPrev, goNext, updateUser }) {
    const {user} = useSelector(state => state.auth);
    const translate = useTranslate();
    const [loading] = useState(false);
    const genderOptions = ["male", "female"];
    const [selectedGender, setSelectedGender] = useState(user?.gender);

    const handleNext = () => {
        updateUser({ gender: selectedGender });
        goNext();
        // setSubIndex(subIndex + 1);
    };

    return (
        <StepShell showPrev={true} onPrev={() => setSubIndex(subIndex - 1)} onNext={handleNext} nextDisabled={!selectedGender}>
            <span className="text-sm text-700 font-bold">
                {translate("init-profile-step-2-sub-step-2-description")}
            </span>
            <Dropdown placeholder={translate("select-gender-placeholder")} className="w-full text-sm rtl" panelClassName="rtl text-xs" itemTemplate={DropdownItemTemplate} valueTemplate={DropdownItemTemplate} loading={loading} options={genderOptions} value={selectedGender} onChange={(e) => setSelectedGender(e.value)}/>
        </StepShell>
    );
}

// function Step2SubStep3({ subIndex, setSubIndex, goNext, updateWorkspace }) {
//     const translate = useTranslate();
//     const [selectedMembersLimit, setSelectedMembersLimit] = useState(null);
//     const items = ["up-to-10", "up-to-20", "up-to-50", "unlimited"];
//
//     const handleNext = () => {
//         updateWorkspace({ membersLimit: selectedMembersLimit });
//         goNext();
//     };
//
//     return (
//         <StepShell showPrev={true} onPrev={() => setSubIndex(subIndex - 1)} onNext={handleNext} nextDisabled={!selectedMembersLimit}>
//             <span className="text-sm text-700 font-bold">{translate("init-workspace-step-2-sub-step-3-description")}</span>
//             <Dropdown placeholder={translate("select-workspace-category-placeholder")} className="w-3" itemTemplate={DropdownItemTemplate} valueTemplate={DropdownItemTemplate} options={items} value={selectedMembersLimit} onChange={(e) => setSelectedMembersLimit(e.value)}/>
//         </StepShell>
//     );
// }

// ----------------------
// Step 3
// ----------------------

// function Step3({ goNext, goPrev, wizardData, updateWorkspace, updateWizardData}) {
//     const [subIndex, setSubIndex] = useState(0);
//
//     const commonProps = {subIndex, setSubIndex, goNext, goPrev, wizardData, updateWorkspace, updateWizardData};
//
//     if (subIndex === 0) return <Step3SubStep1 {...commonProps} />;
//     if (subIndex === 1) return <Step3SubStep2 {...commonProps} />;
//
//     return null;
// }

// function Step3SubStep1({ subIndex, setSubIndex, goPrev, updateWorkspace }) {
//     const translate = useTranslate();
//     const whoAreYouItems = [
//         { name: "manager", key: "manager" },
//         { name: "team-lead", key: "team-lead" },
//         { name: "technical-expert", key: "technical-expert" },
//     ];
//     const [whoAreYou, setWhoAreYou] = useState(null);
//     const handleNext = () => {
//         updateWorkspace({ ownerType: whoAreYou?.key });
//         setSubIndex(subIndex + 1);
//     };
//
//     return (
//         <StepShell showPrev={true} onPrev={goPrev} onNext={handleNext} nextDisabled={!whoAreYou}>
//             <span className="text-sm text-700 font-bold">
//                 {translate("init-workspace-step-3-sub-step-1-description")}
//             </span>
//             <div className="flex flex-column gap-1">
//                 {whoAreYouItems.map((item) => (
//                     <span key={item.key} className="flex gap-1 ltr justify-content-end">
//                         <label className="text-700 text-sm">
//                             {translate(item.name)}
//                         </label>
//                         <RadioButton name="who-are-you" value={item} onChange={(e) => setWhoAreYou(e.value)} checked={whoAreYou?.key === item.key}/>
//                     </span>
//                 ))}
//             </div>
//         </StepShell>
//     );
// }

// function Step3SubStep2({ subIndex, setSubIndex, goNext, updateWorkspace, updateWizardData}) {
//     const translate = useTranslate();
//     const [teams, setTeams] = useState();
//     const [selectedTeams, setSelectedTeams] = useState([]);
//     const [otherTeams, setOtherTeams] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [otherTeam, setOtherTeam] = useState();
//
//     useEffect(() => {
//         const fetchOptionSets = async () => {
//             const optionSet = new OptionSet();
//             setLoading(true);
//             try {
//                 const _data = await optionSet.getAll({});
//                 setTeams(_data.data.teams || []);
//             } catch (e) {
//                 console.log(e);
//             } finally {
//                 setLoading(false);
//             }
//         };
//         fetchOptionSets();
//     }, []);
//
//     const handleNext = () => {
//         updateWizardData({teams: selectedTeams});
//         goNext();
//     };
//
//     return (
//         <StepShell showPrev={true} onPrev={() => setSubIndex(subIndex - 1)} onNext={handleNext} nextDisabled={!selectedTeams || selectedTeams.length < 1}>
//             <span className="text-sm text-700 font-bold">
//                 {translate("init-workspace-step-3-sub-step-2-description")}
//             </span>
//             <div className="flex flex-wrap row-gap-1 align-items-center">
//                 {teams?.concat(otherTeams).map(t => (
//                     <div key={t} className="w-full lg:w-3 rtl flex align-items-center gap-2">
//                         <Checkbox inputId={t} value={t} checked={selectedTeams.includes(t)} onChange={(e) => {
//                                 if (e.checked) {
//                                     setSelectedTeams([...selectedTeams, t]);     // add
//                                 } else {
//                                     setSelectedTeams(selectedTeams.filter(item => item !== t)); // remove
//                                 }
//                             }}
//                         />
//                         <label className="text-700 text-xs" htmlFor={t}>{translate(t)}</label>
//                     </div>
//                 ))}
//             </div>
//             {
//                 selectedTeams.includes("other")&&(
//                     <div className="w-full">
//                         <div className="p-inputgroup ltr w-4">
//                             <span className="p-inputgroup-addon cursor-pointer" onClick={()=>{if (otherTeam)setOtherTeams([...otherTeams, otherTeam]);setSelectedTeams([...selectedTeams, otherTeam]); setOtherTeam("")}}>
//                                 <i className="pi pi-plus"/>
//                             </span>
//                             <InputText placeholder={translate("team-name-placeholder")} className="p-1 rtl text-sm" value={otherTeam} onChange={(e)=>{setOtherTeam(e.target.value)}}/>
//                         </div>
//
//                     </div>
//                 )
//             }
//         </StepShell>
//     );
// }





// function Step3({ goNext, goPrev, updateWorkspace }) {
//     const translate = useTranslate();

//     const handleNext = () => {
//
//         goNext();
//     };
//
//     return (

//     );
// }

// ----------------------
// Step 4
// ----------------------
function Step3({ goNext, goPrev, updateUser }) {
    const translate = useTranslate();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState(null);

    const validatePassword = (_password, _confirmPassword)=>{
        setError(profilePasswordError(_password, _confirmPassword));
        return isProfilePasswordValid(_password, _confirmPassword);
    }

    const handleNext = () => {
        if (validatePassword(password, confirmPassword)){
            updateUser({ password });
            goNext();
            // setSubIndex(subIndex + 1);
        }else {

        }

    };

    return (
        <StepShell showPrev={true} onPrev={goPrev} onNext={handleNext} nextDisabled={!isProfilePasswordValid(password, confirmPassword)}>
            <span className="text-sm text-700 font-bold ">
                {translate("init-profile-step-3-description")}
            </span>
            <Password value={password} className="w-full" inputClassName={`w-full border-3 ${!password && !confirmPassword ? "border-300" : error ? "border-red-500" : "border-green-500"}`} onInput={(e)=>{validatePassword(e.currentTarget.value, confirmPassword);setPassword(e.currentTarget.value); }} placeholder={translate("password")} feedback={false} toggleMask/>
            <Password value={confirmPassword} className="w-full" inputClassName={`w-full border-3 ${!password && !confirmPassword ? "border-300" : error ? "border-red-500" : "border-green-500"}`} onInput={(e)=>{validatePassword(password, e.currentTarget.value);setConfirmPassword(e.currentTarget.value); }} placeholder={translate("confirm-password")} feedback={false} toggleMask/>
            {error&&(<span className="text-red-500 text-xs">{translate(error)}</span>)}
        </StepShell>
    );
}

// ----------------------
// Step 5
// ----------------------
function Step4({ goPrev, wizardData, onSubmit, loading, submitError }) {
    const translate = useTranslate();

    const handleSubmit = async () => {
        await onSubmit?.(wizardData);
    };

    return (
        <StepShell showPrev={true} onPrev={goPrev} onNext={handleSubmit} nextLabel={translate("init-profile-action-finish")} loading={loading}>
            <span className="text-sm text-700 font-bold">
                {translate("init-profile-step-4-description")}
            </span>
            {submitError && <Message className="w-full text-xs" severity="error" text={translate(submitError) === submitError ? translate("profile-onboarding.error") : translate(submitError)}/>}
        </StepShell>
    );
}

export default Init;
